/**
 * จัดเตรียมวัตถุดิบ (Staging) — D-027, D-062, D-063
 *
 * เลือกใบสั่งผลิตที่ปล่อยแล้ว → ดู staged/reserved/consumed แยก product + lot + operation (`SHOP_FLOOR` VIEW) และสร้างรายการ
 * จองวัตถุดิบ (CREATE) การจัดเตรียมคือการ **จอง** ไม่ใช่การเคลื่อน stock จึงไม่มีตัวเลข ledger ในหน้านี้ ปุ่มกดหนึ่งครั้ง = Idempotency-Key หนึ่งตัว
 * (ยิงซ้ำหลังเน็ตหลุดใช้คีย์เดิมจนกว่าฟอร์มจะเปลี่ยน)
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchProductionOrders } from '../../lib/productionOrders/client';
import type { ProductionOrder } from '../../lib/productionOrders/types';
import { newIdempotencyKey, stockFailureText } from '../../lib/inventory/stock';
import {
  createStagingTransfer,
  fetchStaging,
  type StagingRow,
  type StagingTransferInput,
} from '../../lib/inventory/staging';
import { selectablePlants } from '../productionOrders/productionOrderView';

const SHOP_FLOOR_MODULE = 'SHOP_FLOOR';

export interface StagingScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function StagingScreen({ permissions, apiBaseUrl, fetchImpl }: StagingScreenProps) {
  const plants = selectablePlants(permissions);
  const canCreate = can(permissions, SHOP_FLOOR_MODULE, 'CREATE');
  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  const [chosenPlant, setPlant] = useState('');
  const plant = chosenPlant !== '' ? chosenPlant : plants.length === 1 ? (plants[0] ?? '') : '';
  const [orders, setOrders] = useState<readonly ProductionOrder[]>([]);
  const [orderId, setOrderId] = useState('');
  const [rows, setRows] = useState<readonly StagingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    operation_seq: '',
    product_code: '',
    lot_id: '',
    source_location_code: '',
    staging_location_code: '',
    quantity: '',
  });
  const submission = useRef<{ fingerprint: string; key: string } | null>(null);

  useEffect(() => {
    setOrders([]);
    setOrderId('');
    setRows([]);
    if (plant === '') {
      return;
    }
    let active = true;
    void fetchProductionOrders({ plant_code: plant, status: 'RELEASED', limit: 100 }, options).then((result) => {
      if (active && result.status === 'ready') {
        setOrders(result.page.items);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, apiBaseUrl]);

  const reload = async (id: string) => {
    const result = await fetchStaging(id, options);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setRows(result.items);
  };

  const choose = (id: string) => {
    setOrderId(id);
    setRows([]);
    setNotice(null);
    setError(null);
    if (id !== '') {
      void reload(id);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    setError(null);
    const seq = Number(form.operation_seq);
    const qty = form.quantity.trim();
    if (orderId === '' || !Number.isInteger(seq) || seq <= 0 || !/^\d+(\.\d{1,4})?$/.test(qty) || Number(qty) <= 0) {
      setError('เลือกใบสั่งผลิต ระบุ operation และจำนวนมากกว่า 0');
      return;
    }
    if (!form.product_code.trim() || !form.lot_id.trim() || !form.source_location_code.trim() || !form.staging_location_code.trim()) {
      setError('กรอกสินค้า Lot ที่เก็บต้นทาง และที่เก็บ Staging ให้ครบ');
      return;
    }
    const body: StagingTransferInput = {
      plant_code: plant,
      operation_seq: seq,
      product_code: form.product_code.trim().toUpperCase(),
      lot_id: form.lot_id.trim(),
      source_location_code: form.source_location_code.trim().toUpperCase(),
      staging_location_code: form.staging_location_code.trim().toUpperCase(),
      quantity: qty,
    };
    const fingerprint = JSON.stringify([orderId, body]);
    if (submission.current?.fingerprint !== fingerprint) {
      submission.current = { fingerprint, key: newIdempotencyKey() };
    }
    setBusy(true);
    const result = await createStagingTransfer(orderId, body, submission.current.key, options);
    setBusy(false);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    submission.current = null;
    setNotice(`จัดเตรียมแล้ว: ${result.row.product_code} ${result.row.staged_qty} (operation ${result.row.operation_seq})`);
    void reload(orderId);
  };

  const field = (name: keyof typeof form, label: string) => (
    <label className="cap-field">
      <span className="cap-field__label">{label}</span>
      <input
        className="cap-input"
        aria-label={label}
        value={form[name]}
        onChange={(e) => setForm((current) => ({ ...current, [name]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="cap-page" data-testid="staging">
      <div className="cap-page__head">
        <h1 className="cap-page__title">จัดเตรียมวัตถุดิบ (Staging)</h1>
      </div>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">โรงงาน</span>
          <select className="cap-input" aria-label="โรงงาน" value={plant} onChange={(e) => setPlant(e.target.value)}>
            <option value="">— เลือกโรงงาน —</option>
            {plants.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ใบสั่งผลิต (ปล่อยแล้ว)</span>
          <select className="cap-input" aria-label="ใบสั่งผลิต" value={orderId} onChange={(e) => choose(e.target.value)}>
            <option value="">— เลือกใบสั่งผลิต —</option>
            {orders.map((order) => (
              <option key={order.production_order_id} value={order.production_order_id}>
                {order.order_no} — {order.product_code}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="staging-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="staging-notice">
          {notice}
        </p>
      )}

      {orderId !== '' && (
        <table className="cap-table" data-testid="staging-table">
          <thead>
            <tr>
              <th>Op</th>
              <th>สินค้า</th>
              <th>Lot</th>
              <th>ต้นทาง → Staging</th>
              <th style={{ textAlign: 'right' }}>จัดเตรียม</th>
              <th style={{ textAlign: 'right' }}>จอง</th>
              <th style={{ textAlign: 'right' }}>ใช้แล้ว</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8}>ยังไม่มีการจัดเตรียมวัตถุดิบสำหรับใบนี้</td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.staging_id}>
                <td>{row.operation_seq}</td>
                <td>
                  <code className="cap-table__code">{row.product_code}</code>
                </td>
                <td>{row.lot_id.slice(0, 8)}</td>
                <td>
                  {row.source_location_code} → {row.staging_location_code}
                </td>
                <td className="cap-table__num">{row.staged_qty}</td>
                <td className="cap-table__num">{row.reserved_qty}</td>
                <td className="cap-table__num">{row.consumed_qty}</td>
                <td>
                  <StatusBadge tone={row.status === 'STAGED' ? 'info' : 'neutral'}>{row.status}</StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {orderId !== '' && canCreate && (
        <form className="cap-form" data-testid="staging-form" onSubmit={(e) => void submit(e)}>
          <h2 className="cap-notice__title">จัดเตรียมวัตถุดิบเพิ่ม</h2>
          {field('operation_seq', 'Operation')}
          {field('product_code', 'รหัสสินค้า (วัตถุดิบ)')}
          {field('lot_id', 'Lot ID')}
          {field('source_location_code', 'ที่เก็บต้นทาง')}
          {field('staging_location_code', 'ที่เก็บ Staging')}
          {field('quantity', 'จำนวน')}
          <div className="cap-form__actions">
            <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
              {busy ? 'กำลังบันทึก…' : 'จัดเตรียม (จอง)'}
            </button>
          </div>
        </form>
      )}
      {orderId !== '' && !canCreate && (
        <p className="cap-notice__note" data-testid="staging-read-only">
          สิทธิ์ชุดนี้ดูได้อย่างเดียว — ต้องมี <code className="cap-table__code">can_create</code> ของ{' '}
          <code className="cap-table__code">{SHOP_FLOOR_MODULE}</code> จึงจัดเตรียมได้
        </p>
      )}
    </div>
  );
}
